<template lang="pug">
    .csv-import(v-loading="validating || initializing")
        transition-group(name="slide-decent-vertical" mode="out-in")
            .import-info-panel(key="prev-import-info")
                .alert.info(v-if="overview && overview.previous && overview.previous.status === 'failed'")
                    i.el-icon-info
                    .text
                        b {{ $t('The most recent import failed') }}
                        p {{ $t('File: {file}', { file: overview.previous.file.name }) }}
                        p(v-if="overview.previous.error") {{ $t('Error: {error}', { error: overview.previous.error }) }}
            .import-info-panel(v-if="overview && overview.current" key="import-info")
                .fresh(v-if="overview.current.status === 'fresh'")
                    .alert.primary
                        p {{ $t('Your import has been queued and will start as soon as possible.') }}
                        p {{ $t('Please check later') }},&nbsp
                            a(href="#" @click.stop.prevent="refresh") {{ $t('or refresh now') }}
                .pending(v-if="overview.current.status === 'pending'")
                    .alert.primary
                        p {{ $t('An import is in the queue and will start as soon as possible.') }}
                        p {{ $t('Please check later') }},&nbsp
                            a(href="#" @click.stop.prevent="refresh") {{ $t('or refresh now') }}
                .running(v-if="overview.current.status === 'running'")
                    // .percentage
                    //     el-progress(:stroke-width="6" :percentage="Math.round(overview.current.percentage * 10) / 10")
                    .alert.primary
                        .percentage
                            el-progress(
                                type="dashboard"
                                :width="100"
                                :percentage="overview.current.percentage"
                                :format="percentage => `${Math.round(percentage * 100) / 100}%`"
                            )
                        .info(v-if="overview.current.percentage < 100 || overview.current.finished_at === null")
                            b {{ $t('An import is currently running.') }}
                            p {{ $t('File: {file}', { file: overview.current.file.name }) }}
                            // p {{ $t('It has been started {time} ago and is currently at {percentage}%.', { time: formatDistanceToNow(overview.current.started_at), percentage: Math.round(overview.current.percentage * 100) / 100 }) }}
                            p {{ $t('Started: {time}', { time: formatDistanceToNow(overview.current.started_at), percentage: Math.round(overview.current.percentage * 100) / 100 }) }}
                            p(v-if="estimatedCompletion") {{ $t('Estimated completion: {time}.', { time: estimatedCompletion }) }}
                            p {{ $t('Please check later') }},&nbsp
                                a(href="#" @click.stop.prevent="refresh") {{ $t('or refresh now') }}
                        .info(v-else)
                            p {{ $t('The import has been completed.') }}
                //- .failed(v-if="overview.current.status === 'failed'")
                //-     .alert.danger
                //-         p {{ $t('The last import could not be completed successfull. It failed {time} ago.', { time: formatDistanceToNow(overview.current.finished_at) }) }}
                //- .finished(v-if="overview.current.status === 'finished'")
                //-     .alert.success
                //-         p {{ $t('The Last import has been finished successfully {time} ago.', { time: formatDistanceToNow(overview.current.finished_at) }) }}

            el-upload.plus-box(
                key="upload"
                v-if="validationResult === undefined || (validationResult !== undefined && validationResult.status === 'invalid')"
                ref="upload"
                drag :action="uploadEndpoint"
                accept="text/csv"
                :before-upload="validateUpload"
                :on-change="updateUploadHeaders"
                :on-success="uploadSucceed"
                :on-error="uploadFailed"
                :headers="uploadHeaders"
                :data="uploadData"
            )
                i.el-icon-plus
                // i.el-icon-upload.el-icon-fa-cloud-upload-alt.fas
                .el-upload__text(v-html="$t('upload.drop.singular')")
                .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'CSV', size: '20mb' }) }}
            .validation-result(
                v-if="validationResult !== undefined"
                key="validation"
            )
                template(v-if="validationResult.status === 'valid'")
                    .alert
                        p {{ $t('The columns of the uploaded import file could be fully recognized.') }}
                        p {{ $t('You can now start the import.') }}
                    el-button(
                        size="mini"
                        icon="el-icon-fa-times fas"
                        @click="validationResult = undefined"
                    ) {{ $t('Cancel') }}
                    el-button(
                        size="mini"
                        type="primary"
                        icon="el-icon-fa-check fas"
                        @click="start"
                    ) {{ $t('Start import') }}
                .alert.danger(v-else)
                    p
                        //- b {{ $t('The columns of the uploaded import file could not be fully recognized.') }}
                        b {{ $t('The uploaded CSV ist invalid.') }}
                    ol
                        li(v-if="validationResult.missing_identifier === true")
                            p {{ $t('Missing identifier. There must be either an "ID" or an "NR" column.') }}
                        li(v-if="validationResult.missing_sku === true")
                            p {{ $t('Missing SKU. There must be either an "SKU" or an "EAN" column.') }}
                        li(v-if="validationResult.invalid_customer_groups && validationResult.invalid_customer_groups.length")
                            p {{ $t('The following customer groups could not be found in this shop.') }}
                            p {{ $t('Either check the price columns in your import file or create customer groups with these keys.') }}
                            ul
                                li(v-for="key in validationResult.invalid_customer_groups") {{ key }}
                        li(v-if="validationResult.invalid_sale_prices && validationResult.invalid_sale_prices.length")
                            p {{ $t('Some sale price columns are not valid.') }}
                            p {{ $t('For every sale price there need to be three columns: a "sale", a "sale[start]" and a "sale[end]" column.') }}
                            p {{ $t('Check the sale prices with the following customer groups in your import file.') }}
                            ul
                                li(v-for="key in validationResult.invalid_sale_prices")
                                    template(v-if="key != 0") {{ key }}
                                    i.grey(v-else) {{ $t('Sale price without a defined customer group') }}
                        li(v-if="validationResult.duplicate_columns && validationResult.duplicate_columns.length")
                            p {{ $t('The following columns map to the same import field. Please remove the duplicate columns from your import file.') }}
                            ul
                                li(v-for="group in validationResult.duplicate_columns") {{ group }}
                        li(v-if="validationResult.invalid_stocks && validationResult.invalid_stocks.length")
                            p {{ $t('The following stocks could not be found in this shop.') }}
                            p {{ $t('Either check the stock columns in your import file or create stocks with these keys.') }}
                            ul
                                li(v-for="key in validationResult.invalid_stocks") {{ key }}
                        li(v-if="validationResult.invalid_properties && validationResult.invalid_properties.length")
                            p {{ $t('The following properties could not be found in this shop.') }}
                            p {{ $t('Either check the property columns in your import file or create properties with these keys.') }}
                            ul
                                li(v-for="key in validationResult.invalid_properties") {{ key }}
                        li(v-if="validationResult.properties_on_wrong_rows && validationResult.properties_on_wrong_rows.length")
                            p {{ $t('The following property columns carry their values on rows the import does not read them from.') }}
                            p {{ $t('A property either belongs to the product or to its articles. Move the values to the matching rows, or change where the property belongs.') }}
                            ul
                                li(v-for="property in validationResult.properties_on_wrong_rows")
                                    template(v-if="property.value === 'article'") {{ $t('{key}: the values belong on the article rows, the rows with a SKU.', { key: property.key }) }}
                                    template(v-else) {{ $t('{key}: the values belong on the product rows, the rows with a number or an ID.', { key: property.key }) }}
                        li(v-if="validationResult.invalid_fields && validationResult.invalid_fields.length")
                            p {{ $t('The following fields are not supported.') }}
                            ul
                                li(v-for="field in validationResult.invalid_fields") {{ field }}
                        li(v-if="validationResult.invalid_lines && validationResult.invalid_lines.length")
                            p {{ $t('The following lines are invalid. They have an unexpected number of columns.') }}
                            p {{ $t('The expected amount of fields is {number}', { number: validationResult.expected_fields }) }}
                            ul
                                li(v-for="el in validationResult.invalid_lines") {{ $t('Line {line} contains {fields} fields', { line: el.key, fields: el.value }) }}
                    p
                        b {{ $t('Check your import file and try again after said issues has been fixed.') }}
</template>

<i18n>
{
    "en": {
        "It has been started {time} ago and is currently at {percentage}%.": "It has been started {time} ago and is currently at {percentage}%.",
        "File: {file}": "File: {file}",
        "Error: {error}": "Error: {error}",
        "Started: {time}": "Started: {time}",
        "The Last import has been finished successfully {time} ago.": "The Last import has been finished successfully {time} ago.",
        "The last import could not be completed successfull. It failed {time} ago.": "The last import could not be completed successfull. It failed {time} ago.",
        "Estimated completion: {time}.": "Estimated completion: {time}.",
        "Line {line} contains {fields} fields": "Line {line} contains {fields} fields",
        "The expected amount of fields is {number}": "The expected amount of fields is {number}",
        "{key}: the values belong on the article rows, the rows with a SKU.": "{key}: the values belong on the article rows, the rows with a SKU.",
        "{key}: the values belong on the product rows, the rows with a number or an ID.": "{key}: the values belong on the product rows, the rows with a number or an ID."
    },
    "de": {
        "Something went wrong!": "Etwas ist schiefgelaufen!",
        "The uploaded CSV ist invalid.": "Die hochgeladene CSV ist ungültig.",
        "The columns of the uploaded import file could be fully recognized.": "Die Spalten der hochgeladenen Importdatei konnten vollständig erkannt/zugeordnet werden.",
        "The columns of the uploaded import file could not be fully recognized.": "Die Spalten der hochgeladenen Importdatei konnten nicht vollständig erkannt/zugeordnet werden.",
        "You can now start the import.": "Der Import kann jetzt gestartet werden",
        "Start import": "Import starten",
        "Cancel": "Abbrechen",
        "Missing identifier. There must be either an \"ID\" or an \"NR\" column.": "Es fehlt die Spalte zur Kennung des Produkts. Es muss entweder die Spalte \"ID\", oder \"NR\" vorhanden sein",
        "The following customer groups could not be found in this shop.": "Die folgenden Kundengruppen konnten in diesem Shop nicht gefunden werden.",
        "Either check the price columns in your import file or create customer groups with these keys.": "Überprüfen Sie die Preisspalten in Ihrer Importdatei, oder legen Sie Kundengruppen mit den genannten Kennern an.",
        "The following columns map to the same import field. Please remove the duplicate columns from your import file.": "Die folgenden Spalten werden dem gleichen Importfeld zugeordnet. Bitte entfernen Sie die doppelten Spalten aus Ihrer Importdatei.",
        "The following stocks could not be found in this shop.": "Die folgenden Läger konnten in diesem Shop nicht gefunden werden.",
        "Either check the stock columns in your import file or create stocks with these keys.": "Überprüfen Sie die Bestandsspalten in Ihrer Importdatei, oder legen Sie Läger mit den genannten Kennern an.",
        "The following properties could not be found in this shop.": "Die folgenden Attribute konnten in diesem Shop nicht gefunden werden.",
        "The following property columns carry their values on rows the import does not read them from.": "Die folgenden Attributsspalten enthalten ihre Werte in Zeilen, aus denen der Import sie nicht liest.",
        "A property either belongs to the product or to its articles. Move the values to the matching rows, or change where the property belongs.": "Ein Attribut gehört entweder zum Produkt oder zu seinen Artikeln. Verschieben Sie die Werte in die passenden Zeilen, oder ändern Sie die Zugehörigkeit des Attributs.",
        "{key}: the values belong on the article rows, the rows with a SKU.": "{key}: Die Werte gehören in die Artikelzeilen, also die Zeilen mit einer SKU.",
        "{key}: the values belong on the product rows, the rows with a number or an ID.": "{key}: Die Werte gehören in die Produktzeilen, also die Zeilen mit einer Nr. oder einer ID.",
        "Either check the property columns in your import file or create properties with these keys.": "Überprüfen Sie die Attributsspalten in Ihrer Importdatei, oder legen Sie Attribute mit den genannten Kennern an.",
        "Some sale price columns are not valid.": "Einige Angebotspreisspalten sind unvollständig.",
        "For every sale price there need to be three columns: a \"sale\", a \"sale[start]\" and a \"sale[end]\" column.": "Für jeden Angebotspreis müssen drei Spalten vorhanden sein: ein \"Angebotspreis\", ein \"Angebotspreis[von]\" und ein \"Angebotspreis[bis]\" field. ",
        "Check the sale prices with the following customer groups in your import file.": "Überprüfen Sie die Angebotspreise mit den folgenden Kundengruppen.",
        "Sale price without a defined customer group": "Angebotspreis ohne eine Kundengruppendefinition",
        "Check your import file and try again after said issues has been fixed.": "Überprüfen Sie Ihre Importdatei und versuchen Sie es erneut, nachdem die genannten Probleme behoben wurden.",
        "The following fields are not supported.": "Die folgenden Felder werden nicht unterstützt.",
        "The following lines are invalid. They have an unexpected number of columns.": "Die folgenden Zeilen haben eine unerwartete Anzahl an Spalten.",
        "Line {line} contains {fields} fields": "Zeile {line} enthält {fields} Spalten",
        "The expected amount of fields is {number}": "Die erwartete Anzahl an Spalten ist {number}",

        "The most recent import failed": "Der zuletzt ausgeführte Import ist fehlgeschlagen",
        "Technical issue! Product import could not be initialized.": "Technisches Problem! Produktimport konnte nicht initialisiert werden.",
        "Product import could not be refreshed.": "Produktimport konnte nicht aktualisiert werden.",
        "Product import could not be started.": "Produktimport konnte nicht gestartet werden.",
        "An import is in the queue and will start as soon as possible.": "Aktuell befindet sich bereits ein Import in der Warteschlange und wird so schnell wie möglich gestartet.",
        "An import is currently running.": "Aktuell befindet sich bereits ein Import in der Verarbeitung.",
        "It has been started {time} ago and is currently at {percentage}%.": "Der Import wurde vor {time} gestartet und ist aktuell bei {percentage}%.",
        "File: {file}": "Datei: {file}",
        "Error: {error}": "Fehler: {error}",
        "Started: {time}": "Gestartet vor: {time}",
        "The Last import has been finished successfully {time} ago.": "Der letzte import wurde erfolgreich abgeschlossen vor {time}.",
        "The last import could not be completed successfull. It failed {time} ago.": "Der letzte Import konnte nicht erfolgreich abgeschlossen werden. Fehlgeschlagen vor {time}.",
        "Estimated completion: {time}.": "Voraussichtliche Dauer bis zur Fertigstellung: {time}",
        "Your import has been queued and will start as soon as possible.": "Dein Import wurde erfolgreich erfasst und wird so schnell wie möglich gestartet.",
        "Please check later": "Schau doch später nochmal vorbei",
        "The import has been completed.": "Der Import wurde erfolgreich abgeschlossen.",
        "or refresh now": "oder aktualisiere jetzt",
        "Import": "Importieren",
        "Try again": "Erneut versuchen"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { formatDistanceToNow, fromUnixTime, getUnixTime } from 'date-fns';
import { activeDateLocale } from '@/utils/dateLocale';

import { adminClient as apolloClient } from '@/store/apollo';

import { ensureFreshToken } from '@/helpers';

import { useAuthStore, useSetupStore, useShopProductStore } from '@/stores';
import pinia from '@/pinia';

export default {
    name: 'ProductCsvImport',
    data() {
        return {
            file: undefined,
            initializing: false,
            validating: false,
            error: false,
            validationResult: undefined,
            overview: undefined,
        };
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json',
            }
        },
        uploadData() {
            let uploadData = {
                'context[type]': 'original',
            };
            return uploadData;
        },
        estimatedCompletion() {
            if ((!this.overview && this.overview.current) || this.overview.current.percentage <= 0) return false;

            const secondsSinceStarted = getUnixTime(new Date()) - this.overview.current.started_at;
            const estimatedDuration = parseInt((secondsSinceStarted / this.overview.current.percentage) * 100);

            return this.formatDistanceToNow(estimatedDuration + parseInt(this.overview.current.started_at));
        },
        uploadEndpoint() {
            return useSetupStore().uploadEndpoint;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        shopProductStore() {
            return useShopProductStore(pinia);
        },
    },
    methods: {
        formatDistanceToNow(timestamp) {
            return formatDistanceToNow(fromUnixTime(parseInt(timestamp)), { locale: activeDateLocale() });
        },
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken();
        },
        async validateUpload(data) {
            await this.updateUploadHeaders();
            if (data.size / 1024 / 1024 > 20) {
                // this.$message.error('Avatar picture size can not exceed 2MB!');
                this.$notify.error({
                    title: this.$t('upload.sizeExceeded'),
                    message: this.$t('upload.max', { size: '20mb' }),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
                // Reject, never `return false`: an async before-upload always returns a promise,
                // and Element aborts only on rejection - a resolved `false` uploads anyway.
                return Promise.reject();
            }
        },
        async uploadSucceed(response, data) {
            let file = response;
            file.upload = true;
            file.updated_at = Math.round(data.raw.lastModified / 1000);
            file.cms = { id: this.activeCms.id };
            if (typeof file.size === 'number') file.size = file.size.toString();
            delete file.hash;
            delete file.dimensions;

            this.file = file;

            setTimeout(() => {
                this.$refs.upload.uploadFiles = this.$refs.upload.uploadFiles.filter((el) => el.uid !== data.uid);
            }, 100);
            this.validateCsv();

            // store.commit('account/form/administrator/addImage', { i: this.index, image })
            await this.updateUploadHeaders();
        },
        async uploadFailed(err, file) {
            await this.updateUploadHeaders();

            this.$notify.error({
                title: this.$t('upload.notUploaded'),
                message: _.get(file, 'name'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
        validateCsv() {
            if (this.file) {
                this.validating = true;
                const payload = {
                    shopID: _.get(this.activeCms, 'shop.id'),
                    file: this.file,
                };
                this.shopProductStore
                    .validateProductImportCsv(payload)
                    .then((data) => {
                        this.validationResult = data;
                        this.file = data.file;
                    })
                    .catch((error) => {
                        let message;
                        const { graphQLErrors } = error;
                        if (graphQLErrors && graphQLErrors.length > 0) {
                            message = graphQLErrors[0].details;
                        }

                        // eslint-disable-next-line
                        console.error(error);
                        this.error = this.$t('Product import could not be refreshed.');
                        this.$notify.error({
                            title: this.$t('Something went wrong!'),
                            message,
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        });
                    })
                    .finally(() => {
                        this.validating = false;
                    });
            }
        },
        start() {
            if (this.file) {
                this.initializing = true;
                this.shopProductStore
                    .initializeProductImport({
                        shopID: _.get(this.activeCms, 'shop.id'),
                        file: this.file,
                    })
                    .then((data) => {
                        this.error = false;
                        this.overview = data;
                        this.file = undefined;
                        this.validationResult = undefined;
                    })
                    .catch((error) => {
                        // eslint-disable-next-line
                        console.error(error);
                        this.error = this.$t('Technical issue! Product import could not be initialized.');
                        this.$notify.error({
                            title: this.$t('Something went wrong!'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        });
                    })
                    .finally(() => {
                        this.initializing = false;
                    });
            } else {
                this.$notify.error({
                    title: this.$t('Something went wrong!'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
            }
        },
        refresh() {
            apolloClient.clearStore().then(() => {
                this.$emit('refresh');
                this.shopProductStore
                    .initializeProductImport({ shopID: _.get(this.activeCms, 'shop.id') })
                    .then((data) => {
                        this.error = false;
                        this.overview = data;
                        this.file = undefined;
                    })
                    .catch((error) => {
                        // eslint-disable-next-line
                        console.error(error);
                        this.error = this.$t('Product import could not be refreshed.');
                        this.$notify.error({
                            title: this.$t('Something went wrong!'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        });
                    })
                    .finally(() => {
                        this.initializing = false;
                    });
            });
        },
    },
    mounted() {
        this.initializing = true;
        this.shopProductStore
            .initializeProductImport({ shopID: _.get(this.activeCms, 'shop.id') })
            .then((data) => {
                this.overview = data;
            })
            .finally(() => {
                this.initializing = false;
            });
    },
};
</script>

<style lang="sass" scoped>
.upload
    width: 100%

.alert.danger
    ul
        margin: 0.5rem 0
    & > *:last-child
        margin-bottom: 0

.running .alert
    display: flex
    align-items: center
    .percentage
        margin-right: 1rem
        margin-bottom: -1rem
        ::v-deep
            .el-progress__text
                padding-left: 0.5rem
            path.el-progress-circle__track
                stroke: #dad4e5 !important
.alert.info
    display: flex
    align-items: center
    i
        opacity: 0.6
        min-width: 2rem
        font-size: 1.4rem
</style>
